import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import client from '../shopify';
import ProductCard from '../components/ProductCard';

//
// Same idea: replicate Home's structure and container
//
const Container = styled.div`
  padding: 40px;
`;

const Section = styled.div`
  margin-bottom: 60px; /* Extra spacing between sections */
`;

const SectionTitle = styled.h2`
  font-size: 1.5em;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 20px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 20px;
  margin-bottom: 40px; /* Space between products and button */
  
  /* Responsive grid adjustments */
  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const ViewMoreButton = styled.button`
  display: block;
  width: 100%;
  max-width: 300px;
  padding: 15px;
  margin: 75px auto 0;
  background-color: #000;
  color: #fff;
  font-size: 1em;
  font-weight: bold;
  border: none;
  cursor: pointer;
  text-transform: uppercase;
  transition: background 0.3s;

  &:hover {
    background-color: #333;
  }
`;

const Collections = ({ sortOrder }) => {
  const [collections, setCollections] = useState([]);
  
  // Custom product order as specified by user - using exact titles from Shopify admin
  const customProductOrder = [
    "Chalk Zip Up",
    "Black Zip Up",
    "Navy Pullover Hoodie",
    "Black Guinea Tee",
    "Navy Guinea Tee",
    "Chalk Guinea Tee",
    "White Guinea Tee",
    "Chalk Boxy Tee Shirt",
    "White Boxy Tee Shirt",
    "Black Boxy T-Shirt",
    "Navy Cuffed Sweatpant",
    "Chalk Flared Sweatpant"
  ];

  useEffect(() => {
    const fetchCollections = async () => {
      const collectionsQuery = `
        {
          collections(first: 5) {
            edges {
              node {
                id
                title
                products(first: 20, sortKey: TITLE) {
                  edges {
                    node {
                      id
                      title
                      handle
                      images(first: 1) {
                        edges {
                          node {
                            src
                          }
                        }
                      }
                      priceRange {
                        minVariantPrice {
                          amount
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      try {
        const response = await client.request(collectionsQuery);
        setCollections(response.data.collections.edges);
      } catch (error) {
        console.error('Error fetching collections:', error);
      }
    };

    fetchCollections();
  }, []);

  const sortedCollections = collections.map(({ node: collection }) => {
    const sortedItems = [...collection.products.edges].sort((a, b) => {
      if (sortOrder === 'price-asc') {
        return a.node.priceRange.minVariantPrice.amount - b.node.priceRange.minVariantPrice.amount;
      } else if (sortOrder === 'price-desc') {
        return b.node.priceRange.minVariantPrice.amount - a.node.priceRange.minVariantPrice.amount;
      } else {
        // Default to custom order
        const indexA = customProductOrder.findIndex(title => title === a.node.title);
        const indexB = customProductOrder.findIndex(title => title === b.node.title);
        
        // If both products are in the custom order list
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        
        // If only product A is in the list, it comes first
        if (indexA !== -1) return -1;
        
        // If only product B is in the list, it comes first
        if (indexB !== -1) return 1;
        
        // If neither product is in the list, maintain original order
        return 0;
      }
    });
    return { ...collection, products: { edges: sortedItems } };
  });

  return (
    <>
      <Container>
        {sortedCollections.map((collection) => (
          <Section key={collection.id}>
            <SectionTitle>{collection.title}</SectionTitle>
            <ProductsGrid>
              {collection.products.edges.map(({ node: product }) => (
                <ProductCard
                  key={product.id}
                  id={product.handle}
                  image={product.images.edges[0]?.node.src}
                  title={product.title}
                  price={product.priceRange.minVariantPrice.amount}
                />
              ))}
            </ProductsGrid>
            <ViewMoreButton>View More</ViewMoreButton>
          </Section>
        ))}
      </Container>
    </>
  );
};

export default Collections;